import { CreateUnverifiedUserComponent } from './../create-unverified-user/create-unverified-user.component';
import { OfficialService } from './official.service';
import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, Signal, ViewChild, computed, inject, signal } from '@angular/core';
import { AlertController, IonAccordionGroup, IonicModule, ItemReorderEventDetail, ModalController, PopoverController } from '@ionic/angular';
import { Tournament } from '../../models/tournament.model';
import { SelectUserComponent } from '../select-user/select-user.component';
import { Observable, Subject, of, filter, merge } from 'rxjs';
import { map, scan, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { TournamentDetailsService } from '../../../tournaments/tournament-details/tournament-details.service';
import { FormsModule } from '@angular/forms';
import { EventOfficial } from '../../models/event-official.model';
import { MatchUpStatusEnum } from '../../models/csTypes/enums/matchup_status';
import { ScoreboardService } from '../../../scoreboard/scoreboard.service';
import { IocSelectorOption, IocSelectorService } from '../ioc-selector/ioc-selector.service';
import { EmptyPageModule } from '../../../empty-page/empty-page.module';
import { Preferences } from '@capacitor/preferences';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-officials',
  templateUrl: './officials.component.html',
  styleUrls: ['./officials.component.scss'],
  standalone: true,
  imports: [
    IonicModule,
    CommonModule,
    FormsModule,
    EmptyPageModule,
    TranslateModule 
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OfficialsComponent implements OnInit, OnDestroy {
  private modalController = inject(ModalController);
  private alertController = inject(AlertController);
  private officialService = inject(OfficialService);
  private tournamentDetailsService = inject(TournamentDetailsService);
  private datePipe = inject(DatePipe);
  private iocSelectorService = inject(IocSelectorService);
  scoreboardService = inject(ScoreboardService);

  @ViewChild('accordionGroup', { static: true }) accordionGroup: IonAccordionGroup;


  @Input({ required: true }) _tournamentId: string;
  @Input({ required: true }) isEdit: boolean;
  @Input({ required: true }) available: string = 'ALL';

  selectedVenue: string = 'ALL';

  private refreshOfficial$ = new Subject<void>();
  private readonly insertedOfficialSource$ = new Subject<EventOfficial>();
  private readonly insertedOfficial$ = this.insertedOfficialSource$.asObservable();

  officials$: Observable<EventOfficial[]> = merge(
    this.refreshOfficial$.pipe(
      startWith(undefined),
      filter(() => this._tournamentId != null),
      switchMap(() => {
        return this.officialService.indexTournament(this._tournamentId);
      })
    ),
    this.insertedOfficial$
  ).pipe(
    scan((officials, newOfficial) => [...officials, newOfficial])
  )

  tournament$: Observable<Tournament>;

  officials: Signal<EventOfficial[]> = toSignal(this.officials$);

  filterValue = signal('');

  filteredOfficials: Signal<EventOfficial[]> = computed(() => {
    if (!this.filterValue()) {
      return this.officials();
    } else {
      return this.officials().filter(official => {
        return (
          (official.user?.first_name?.toLowerCase().indexOf(this.filterValue().toLowerCase()) > -1) ||
          (official.user?.last_name?.toLowerCase().indexOf(this.filterValue().toLowerCase()) > -1)
        );
      })
    }
  });

  loaded = computed(() => {
    return !!this.officials()
  })

  isAddingOfficial: boolean;

  private readonly destroy$: Subject<void> = new Subject();

  async ngOnInit() {
    this.refreshOfficial$.next();
    this.tournament$ = this.tournamentDetailsService.getTournament(this._tournamentId, true);

    const selectedVenue = (await Preferences.get({ key: `set-venue-${this._tournamentId}` }))?.value;

    if (selectedVenue) {
      this.selectedVenue = selectedVenue
    }
  }

  async ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async chooseAddOfficialMethod() {
    const title = 'Add Official Method';
    const showCancelButton = true;
    const iocSelectorOptions: IocSelectorOption[] = [
      {
        label: 'Phone',
        value: 'PHONE'
      },
      {
        label: 'Create new',
        value: 'CREATE_NEW'
      },
    ]

    const method: string = await this.iocSelectorService.openPopover(title, iocSelectorOptions, showCancelButton);

    if (method === 'PHONE') {
      await this.addOfficialByPhone();
    } else if (method === 'CREATE_NEW') {
      await this.addUnverifiedOfficial()
    }
  }

  async addUnverifiedOfficial() {
    const modal = await this.modalController.create({
      component: CreateUnverifiedUserComponent,
      presentingElement: await this.modalController.getTop()
    });

    await modal.present();

    const { data } = await modal.onDidDismiss();

    if (data) {
      // this.isAddingOfficial = true;
      this.addOfficial(null, data._id);
    }
  }

  async addOfficialByPhone() {
    const modal = await this.modalController.create({
      component: SelectUserComponent,
      presentingElement: await this.modalController.getTop()
    });

    await modal.present();

    const { data } = await modal.onDidDismiss();

    if (data) {
      this.isAddingOfficial = true;
      const phone = data.country_code + data.phone;
      this.addOfficial(phone)
    }
  }

  addOfficial(phone?: string, _userId?: string) {
    this.officialService
      .addToTournament(this._tournamentId, phone, _userId)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(
        official => {
          this.insertedOfficialSource$.next(official);
        }
      )
  }

  async removeOfficial(_userId: string, index: number) {

    const alert = await this.alertController.create({
      header: 'Remove official',
      message: 'By removing the official, you will lose information about their work for this event, but they will not be removed from the matches where they were previously assigned. This action cannot be undone. Do you want to proceed with removing the official?',
      buttons: [{
        text: 'Remove',
        role: 'destructive',
        handler: () => {
          this.officialService
            .removeFromTournament(this._tournamentId, _userId)
            .pipe(
              takeUntil(this.destroy$)
            )
            .subscribe(
              () => {
                this.officials().splice(index, 1);
              }
            )
        }
      }, {
        text: 'Cancel',
        role: 'cancel'
      }]
    });

    await alert.present();

  }

  setAvailability(ev, index) {
    const official = this.officials()[index];
    this.officialService
      .setAvailability(this._tournamentId, official.user._id, ev.detail.checked)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(
        () => {
          official.isAvailable = ev.detail.checked;
        }
      )
  }

  async selectOfficial(official) {
    await this.modalController.dismiss(official);
  }

  async setVenue(ev) {
    this.selectedVenue = ev.detail.value;

    await Preferences.set({
      key: `set-venue-${this._tournamentId}`,
      value: ev.detail.value
    })
  }

  applyFilter(offcial: EventOfficial): boolean {
    if ((this.available === 'ALL' ||
      (offcial.isAvailable && this.available === 'AVAILABLE') ||
      (!offcial.isAvailable && this.available === 'NOT_AVAILABLE')) &&
      (this.selectedVenue === 'ALL' || (this.selectedVenue && this.selectedVenue === offcial.venue?._id))
    ) {
      return true;
    }

    return false;
  }

  getStartDate(official: EventOfficial): string {
    if (!official.currentMatchAssignment) return 'No assignment';
    else if (official.currentMatchAssignment.matchStatus === 'TBP') return 'TBP';
    else return this.datePipe.transform(official.currentMatchAssignment?.startDate, 'MMM d h:mm a')
  }

  getEndDate(official: EventOfficial): string {
    if (!official.currentMatchAssignment || official.currentMatchAssignment.matchStatus === 'TBP') return '';
    else if (official.currentMatchAssignment.matchStatus === MatchUpStatusEnum.IN_PROGRESS) return ' - In progress';
    else return ` - ${this.datePipe.transform(official.currentMatchAssignment?.endDate, 'MMM d h:mm a')}`;
  }

  getCourtName(official: EventOfficial): string {
    if (official.currentMatchAssignment) {
      const courtName = this.scoreboardService.getCourtName(official.currentMatchAssignment);
      if (courtName) return `(${courtName})`;
    }

    return null;
  }

  onSearchChange(ev) {
    const val = ev.target.value;
    val.trim();
    this.filterValue.set(val);
  }

  close() {
    this.modalController.dismiss();
  }

  updateVenue(ev, _officialId) {
    this.officialService.setVenue(this._tournamentId, _officialId, ev.detail.value).subscribe()

  }

}
